import axios from "axios";

// Use relative /api path if running on a different domain than build-time URL
// This ensures the app works on custom domains (e.g. glpflo.org) after deployment
const buildUrl = process.env.REACT_APP_BACKEND_URL || "";
const isSameDomain = buildUrl && window.location.origin === new URL(buildUrl).origin;
const baseURL = isSameDomain ? `${buildUrl}/api` : "/api";

const API = axios.create({
  baseURL,
  withCredentials: true,
});

// ===== Tab-scoped session isolation =====
// On login, the backend returns `tab_token`; we stash it in sessionStorage
// (tab-scoped by design). Every authenticated request echoes it via the
// `X-Tab-Token` header. Opening a new tab → no token → backend returns 401
// → forced to /login. Closing the tab clears sessionStorage → same result.
// Refreshing the SAME tab preserves sessionStorage → stays logged in.
// This prevents cross-tab / cross-portal credential bleed (e.g. a stale
// staff tab inheriting a freshly-logged-in patient's cookies).
const TAB_TOKEN_KEY = "glpflo-tab-token";
const BEARER_TOKEN_KEY = "glpflo-bearer-token";

export function setTabToken(token) {
  try {
    if (token) sessionStorage.setItem(TAB_TOKEN_KEY, token);
    else sessionStorage.removeItem(TAB_TOKEN_KEY);
  } catch (_) { /* privacy-mode may disable sessionStorage */ }
}
export function getTabToken() {
  try { return sessionStorage.getItem(TAB_TOKEN_KEY) || ""; }
  catch { return ""; }
}
export function clearTabToken() {
  try { sessionStorage.removeItem(TAB_TOKEN_KEY); } catch { /* ignore */ }
}

// Bearer JWT (used when a mobile client hands off to the WebView — cookies
// don't cross the Expo → web boundary, but a Bearer header does). The
// backend accepts EITHER cookie OR Authorization: Bearer.
export function setBearerToken(token) {
  try {
    if (token) sessionStorage.setItem(BEARER_TOKEN_KEY, token);
    else sessionStorage.removeItem(BEARER_TOKEN_KEY);
  } catch (_) { /* ignore */ }
}
export function getBearerToken() {
  try { return sessionStorage.getItem(BEARER_TOKEN_KEY) || ""; }
  catch { return ""; }
}
export function clearBearerToken() {
  try { sessionStorage.removeItem(BEARER_TOKEN_KEY); } catch { /* ignore */ }
}

// Auto-capture tab_token from any login response + attach to every request.
API.interceptors.request.use((config) => {
  const tok = getTabToken();
  if (tok) {
    config.headers = config.headers || {};
    config.headers["X-Tab-Token"] = tok;
  }
  const bearer = getBearerToken();
  if (bearer) {
    config.headers = config.headers || {};
    // Backend get_current_user accepts either cookie or Bearer.
    config.headers["Authorization"] = `Bearer ${bearer}`;
  }
  return config;
});

API.interceptors.response.use(
  (res) => {
    // Auto-capture tab_token from any endpoint that returns one (login,
    // register, mfa-verify, employee-login). Callers don't have to
    // remember to stash it — just calling the login endpoint is enough.
    try {
      if (res?.data && typeof res.data.tab_token === "string" && res.data.tab_token) {
        setTabToken(res.data.tab_token);
      }
    } catch (_) { /* ignore */ }
    return res;
  },
  async (error) => {
    const originalRequest = error.config;
    // Only attempt refresh + redirect when (a) we actually have a session
    // token (otherwise the visitor is anonymous and this 401 is expected),
    // (b) the failing request isn't itself an auth endpoint (would loop),
    // and (c) we haven't already retried this request. An anonymous visitor
    // hitting a protected endpoint (e.g. /chat/conversations/mine while
    // logged out, or any admin endpoint that 401s for an unauth visitor)
    // must NOT trigger a full-page redirect — that breaks the landing
    // page (bounces to /login after site gate) and creates a blank-screen
    // flash right after employee login if any post-login fetch 401s once.
    const isAuthUrl = (originalRequest.url || "").includes("/auth/");
    const hadSession = !!getTabToken();
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthUrl &&
      hadSession
    ) {
      originalRequest._retry = true;
      try {
        await API.post("/auth/refresh");
        return API(originalRequest);
      } catch {
        // Refresh failed → session genuinely expired. Clear the tab token
        // and bounce to /login (unless we're already on a public/staff
        // login page, in which case just clear and let the page handle it).
        clearTabToken();
        clearBearerToken();
        const path = typeof window !== "undefined" ? window.location.pathname : "";
        const isOnLoginScreen = path === "/login" || path === "/cliniclogin" || path === "/doctordoctor";
        if (!isOnLoginScreen && typeof window !== "undefined") {
          window.location.href = "/login";
        }
      }
    }
    // FastAPI returns structured `detail` payloads for some errors (e.g. our
    // LOCKED_FEATURE 403s use {code, feature, message}; Pydantic 422s return
    // arrays of {msg, ...}). Many components do `toast.error(e.response.data.detail || ...)`
    // which crashes React if detail is an object/array. Pre-flatten to a string
    // here so every call site is safe by default. We preserve the original
    // structure on `error.response.data._detail` for any caller that needs it.
    try {
      const data = error.response?.data;
      if (data && data.detail != null && typeof data.detail !== "string") {
        const detail = data.detail;
        let flat = "";
        if (Array.isArray(detail)) {
          flat = detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : null))
            .filter(Boolean)
            .join(" · ");
        } else if (typeof detail === "object" && typeof detail.message === "string") {
          flat = detail.message;
        }
        if (flat) {
          data._detail = detail;
          data.detail = flat;
        }
      }
    } catch (_) { /* never let the interceptor itself throw */ }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default API;
